// @flow

import * as React from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { get, head, toString } from 'lodash/fp';
import { connect } from 'react-redux';
import numeral from 'numeral';
import pluralize from 'pluralize';

import Page from 'app/containers/parts/Page';
import t from 'app/utils/translate';
import * as h2hActions from 'app/actions/h2hActions';
import * as challengeActions from 'app/actions/challengeActions';
import { startLobbyCustomPoll, startLobbyH2HPoll } from 'app/actions/lobbyActions';
import Columns from 'app/components/commons/Columns';
import InputField from 'app/components/commons/InputField';
import Section from 'app/components/commons/Section';
import FlexiSelect from 'app/components/commons/FlexiSelect';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import SportsTab from './parts/SportsTab';
import PrizesSection from './parts/PrizesSection';
import ContestTypeSection from './parts/ContestTypeSection';
import FooterSection from './parts/FooterSection';
import ContestSlider from './parts/Slider';
import { Grid, PriceButton } from './parts/components';
import CreateChallengeLineUpBuilder from './CreateChallengeLineUpBuilder';
import SuccessModal from './SuccessModal';

type Props = {
  allowedEntryCosts: Array<number>,
  challengeIds: Array<string>,
  challengeSlateId: ?string,
  darkMode: boolean,
  errorMessages: Array<string>,
  fetchSlate: (id: string) => void,
  isCreating: boolean,
  isLoading: boolean,
  h2h: Object,
  multiPlayers: Object,
  numChallengesEntered: number,
  refreshCustomChallenges: () => void,
  refreshH2HChallenges: () => void,
  resetChallenge: () => void,
  siteUrl: string,
  slates: Array<Object>,
  sportsList: Array<Object>
};

const Wrapper = styled.div(({ theme }) => ({
  overflow: 'hidden',
  width: '100vw',
  [theme.mediaQuery.desktopAndUp]: {
    width: 'auto'
  }
}));

const Container = styled.div(({ numPages, pageNo }) => ({
  display: 'flex',
  transform: `translateX(${((1 - pageNo) * 100) / numPages}%)`,
  transition: '0.3s all ease-in-out',
  width: `${numPages * 100}%`
}));

const MainSection = styled.div(({ selected }) => ({
  opacity: selected ? 1 : 0,
  flexBasis: '50%',
  transition: '0.2s opacity',
  maxWidth: '50%'
}));

const Details = styled.div(({ disabled }) => ({
  opacity: disabled ? 0.5 : 1
}));

const Text = styled.p(({ theme }) => ({
  padding: theme.spacing.tiny
}));

const getPrizeStructureOptions = (prizeStructure = {}) =>
  Object.entries(prizeStructure).map(([value, data]) => ({
    value,
    label: data.label
  }));

const getContestSizeOptions = ({ min = 1 } = {}) => {
  const options = [];
  const start = Math.min(min, 10);
  const end = 10;

  for (let i = start; i <= end; i += 1) {
    options.push({ value: i, label: toString(i) });
  }

  [15, 20, 30, 50, 75, 100, 200].forEach(size =>
    options.push({ value: size, label: toString(size) })
  );
  return options;
};

const getMaxPerUserOptions = ({ min, max }) => {
  const options = [];

  if (!min || !max) {
    return options;
  }
  for (let i = min; i <= 1; i += 1) {
    options.push({ value: i, label: toString(i) });
  }
  return options;
};

const displayEntryFee = fee => (fee === 0 ? 'FREE' : `$${fee}`);

const getDefaultContestName = ({ slate, entryFee, appendix }) =>
  slate ? `${get('name', slate)} ${displayEntryFee(entryFee)}${appendix}` : '';

const getQtyRange = ({ contestType, entryFee, isPrivate, h2h }) => {
  let minQty = 1;
  let maxQty = 1;

  if (contestType === 'h2h') {
    minQty = entryFee === 0 ? get('qtyFree.min', h2h) : get('qty.min', h2h);
    maxQty = entryFee === 0 ? get('qtyFree.max', h2h) : get('qty.max', h2h);

    if (isPrivate) {
      maxQty = 1;
    }
  } else if (contestType === 'multi_players') {
    minQty = 1;
    maxQty = 1;
  }

  return { minQty, maxQty };
};

const CreateChallenge: React.FC<Props> = ({
  sportsList,
  slates,
  allowedEntryCosts,
  challengeSlateId,
  challengeIds,
  darkMode,
  errorMessages,
  fetchSlate,
  isCreating,
  isLoading,
  h2h,
  multiPlayers,
  numChallengesEntered,
  refreshCustomChallenges,
  refreshH2HChallenges,
  resetChallenge,
  siteUrl
}: Props) => {
  const prizeStructureOptions = getPrizeStructureOptions(multiPlayers.prizeStructure);
  const maxPerUserOptions = getMaxPerUserOptions({
    min: get('qtyPerUser.min', multiPlayers),
    max: get('qtyPerUser.max', multiPlayers)
  });

  /**
   * States
   */

  const [selectedSport, setSelectedSport] = React.useState(sportsList[0]);
  const [selectedSlate, setSelectedSlate] = React.useState();

  const [entryFee, setEntryFee] = React.useState(10);
  const [qty, setQty] = React.useState(1);
  const [pageNo, setPageNo] = React.useState(1);
  const [contestType, setContestType] = React.useState('h2h');
  const [isPrivate, setIsPrivate] = React.useState(false);
  const [contestName, setContestName] = React.useState(
    getDefaultContestName({
      entryFee,
      slate: selectedSlate,
      appendix: contestType === 'h2h' ? ' H2H' : ''
    })
  );
  const [selectedPrizeStructure, setSelectedPrizeStructure] = React.useState(
    get('[0].value', prizeStructureOptions)
  );
  const [numEntries, setNumEntries] = React.useState(
    get(`${selectedPrizeStructure}.maximum_entries.min`, multiPlayers.prizeStructure)
  );
  const [selectedMaxPerUser, setSelectedMaxPerUser] = React.useState(
    get('[0].value', maxPerUserOptions)
  );
  const [prizes, setPrizes] = React.useState([]);
  const [openModal, setOpenModal] = React.useState(false);

  const { minQty, maxQty } = getQtyRange({ contestType, entryFee, h2h, isPrivate });

  const minNumEntries =
    get(`${selectedPrizeStructure}.maximum_entries.min`, multiPlayers.prizeStructure) || 1;
  const maxNumEntries =
    get(`${selectedPrizeStructure}.maximum_entries.max`, multiPlayers.prizeStructure) || 1;

  // Filtered slates
  const [filteredSlates, slateOptions] = React.useMemo(() => {
    if (!selectedSport || !selectedSport.filterKey) return [[], []];

    const slatesForSport = slates.filter(slate => {
      if (slate[selectedSport.filterKey])
        return selectedSport.filterValues.includes(slate[selectedSport.filterKey]);
    });

    const slateOpts = slatesForSport.map(({ id, name, startTime }) => ({
      value: id,
      label: `${name} - ${moment(startTime).format('dddd h:mm a')}`
    }));

    return [slatesForSport, slateOpts];
  }, [selectedSport, slates]);

  React.useEffect(() => {
    setSelectedSlate(head(filteredSlates));
  }, [filteredSlates]);

  const contestSizeOptions = getContestSizeOptions(
    get(`${selectedPrizeStructure}.maximum_entries`, multiPlayers.prizeStructure)
  );

  const checkNumEntries = () => {
    if (numEntries < minNumEntries) {
      setNumEntries(minNumEntries);
    } else if (numEntries > maxNumEntries) {
      setNumEntries(maxNumEntries);
    }
  };

  const checkQty = () => {
    if (qty < minQty) {
      setQty(minQty);
    } else if (qty > maxQty) {
      setQty(maxQty);
    }
  };

  /**
   * Effects
   */

  React.useEffect(() => {
    if (!isCreating && numChallengesEntered > 0) {
      setPageNo(1);
      setOpenModal(true);
    }
  }, [isCreating, numChallengesEntered]);

  React.useEffect(resetChallenge, [selectedSlate]);

  React.useEffect(() => setPageNo(challengeSlateId ? 2 : 1), [challengeSlateId]);

  // Reset contest name to default when selected slate, entry fee or contest visibility changes
  React.useEffect(() => {
    setContestName(
      getDefaultContestName({
        entryFee,
        slate: selectedSlate,
        appendix: contestType === 'h2h' ? ' H2H' : ''
      })
    );
  }, [contestType, selectedSlate, entryFee, isPrivate]);

  // Check numEntries when the range changes
  React.useEffect(checkNumEntries, [minNumEntries, maxNumEntries]);
  // Check qty when the range changes
  React.useEffect(checkQty, [minQty, maxQty]);

  const totalFee = entryFee * qty;
  const hasError = errorMessages.length > 0;

  // Name of private contest is editable
  const canEditContestName = isPrivate;

  const handleSelectSlate = slateId => setSelectedSlate(filteredSlates.find(s => s.id === slateId));

  const handleSelectSport = sport => {
    setSelectedSport(sport);
  };

  const handleSelectTeam = () => {
    if (challengeSlateId && toString(challengeSlateId) === toString(get('id')(selectedSlate))) {
      setPageNo(2);
      return;
    }
    fetchSlate(get('id', selectedSlate));
  };

  const handleCloseModal = () => {
    resetChallenge();
    refreshCustomChallenges(true);
    refreshH2HChallenges(true);
    setOpenModal(false);
  };

  if (slates.length === 0) {
    return (
      <Page title={t('Create Challenge')}>
        <h3 className="has-text-centered">No slates available. Please check back later.</h3>
      </Page>
    );
  }

  const disabled = !selectedSlate;
  const shareLinks = contestType !== 'h2h' || isPrivate;

  return (
    <Page
      title={t('Create Challenge')}
      onBack={pageNo > 1 ? () => setPageNo(pageNo - 1) : undefined}
    >
      <Wrapper>
        <Container pageNo={pageNo} numPages={2}>
          <MainSection selected={pageNo === 1}>
            <SportsTab
              onSelect={handleSelectSport}
              selectedSport={selectedSport}
              sportsList={sportsList}
            />
            <Section title="Select Slate">
              <FlexiSelect
                loading={false}
                onChange={handleSelectSlate}
                options={slateOptions}
                placeholder="Select Slate"
                value={get('id')(selectedSlate)}
              />
            </Section>
            <Details disabled={disabled}>
              <ContestTypeSection
                contestType={contestType}
                isPrivate={isPrivate}
                onSelectContestType={setContestType}
                onSelectPrivate={setIsPrivate}
                disabled={disabled}
              />
              <Section title="Entry Fee">
                <Grid>
                  {allowedEntryCosts.map(fee => (
                    <PriceButton
                      key={fee}
                      selected={entryFee === fee}
                      onClick={() => (!disabled ? setEntryFee(fee) : undefined)}
                    >
                      {fee > 0 ? numeral(fee).format('$0,0') : 'FREE'}
                    </PriceButton>
                  ))}
                </Grid>
              </Section>
              <Columns>
                <div className="column is-half">
                  <Section title={`${t('Challenge')} Name`}>
                    <InputField
                      lightContent={!darkMode}
                      noMargin
                      autoBorderRadius={false}
                      type="text"
                      name="contestName"
                      onChange={setContestName}
                      value={contestName}
                      disabled={!canEditContestName || disabled}
                      maxlength={255}
                    />
                  </Section>
                </div>
                <div className="column is-half">
                  <Section
                    title={
                      contestType === 'multi_players'
                        ? `${t('challenge')} size`
                        : 'number of contests to create'
                    }
                  >
                    {contestType === 'h2h' && (
                      <ContestSlider
                        disabled={disabled}
                        min={minQty}
                        max={maxQty}
                        value={qty}
                        onChange={setQty}
                      />
                    )}
                    {contestType === 'multi_players' && (
                      <FlexiSelect
                        loading={false}
                        disabled={disabled}
                        onChange={setNumEntries}
                        options={contestSizeOptions}
                        value={numEntries}
                        placeholder="Select contest size"
                      />
                    )}
                  </Section>
                </div>
              </Columns>
              {contestType === 'multi_players' && (
                <Columns>
                  <div className="column is-half">
                    <Section title="Prize Structure">
                      <FlexiSelect
                        loading={false}
                        disabled={disabled}
                        onChange={setSelectedPrizeStructure}
                        options={prizeStructureOptions}
                        placeholder="Select prize structure"
                        value={selectedPrizeStructure}
                      />
                    </Section>
                  </div>
                  <div className="column is-half">
                    <Section title="Max Entries Per Person">
                      <FlexiSelect
                        loading={false}
                        disabled
                        onChange={setSelectedMaxPerUser}
                        options={maxPerUserOptions}
                        placeholder="Select prize structure"
                        value={selectedMaxPerUser}
                      />
                    </Section>
                  </div>
                </Columns>
              )}
              <PrizesSection
                contestType={contestType}
                entryFee={entryFee}
                selectedPrizeStructure={selectedPrizeStructure}
                minEntries={minNumEntries}
                maxEntries={maxNumEntries}
                numEntries={numEntries}
                didFetch={setPrizes}
              />
              <FooterSection
                isLoading={isLoading}
                disabled={!selectedSlate || isLoading}
                qty={qty}
                onSelectTeam={handleSelectTeam}
                totalFee={totalFee}
              />
              <Text>
                If your contest does not reach the minimum entries required for the prize structure
                it will automatically change to suit. This will happen when a contest goes live. See
                T &amp; C’s for more information.
              </Text>
            </Details>
          </MainSection>
          <MainSection selected={pageNo === 2}>
            {pageNo === 2 && (
              <CreateChallengeLineUpBuilder
                challengeName={contestName}
                challengeType={contestType}
                disableSelectLineup
                isPrivate={isPrivate}
                slateId={get('id')(selectedSlate)}
                maxNumEntries={contestType === 'multi_players' ? numEntries : undefined}
                maxNumEntriesPerUser={
                  contestType === 'multi_players' ? selectedMaxPerUser : undefined
                }
                cost={totalFee}
                entryFee={entryFee}
                numChallengesToCreate={qty}
                onBack={() => setPageNo(1)}
                prizes={prizes}
                disableTabs={['entries']}
                prizeStructure={selectedPrizeStructure}
              />
            )}
          </MainSection>
        </Container>
      </Wrapper>
      {openModal && (
        <SuccessModal
          isOpen
          onCancel={handleCloseModal}
          links={
            shareLinks
              ? challengeIds.map(id =>
                  [siteUrl, 'fantasy', t('challenge').toLowerCase(), id].join('/')
                )
              : []
          }
        />
      )}
      <NotificationHandler
        afterCond={!isCreating && hasError}
        beforeCond={isCreating}
        persist={hasError}
        type="danger"
      >
        <span>
          Some {pluralize(t('challenge'), numChallengesEntered)} failed.
          <br />
          {errorMessages.join('\n')}
        </span>
      </NotificationHandler>
    </Page>
  );
};

const mapStateToProps = state => ({
  isLoading: state.challenge.isLoading,
  challengeSlateId: state.challenge.id,
  isCreating: state.lineUp.isSubmitting,
  errorMessages: state.lineUp.errorMessages,
  numChallengesEntered: state.lineUp.numChallengesEntered,
  challengeIds: state.lineUp.challengeIds || [],
  darkMode: state.settings.darkMode,
  siteUrl: state.settings.siteUrl
});

const actions = {
  fetchSlate: h2hActions.fetchSlate,
  resetChallenge: challengeActions.resetChallenge,
  refreshCustomChallenges: startLobbyCustomPoll,
  refreshH2HChallenges: startLobbyH2HPoll
};

export default connect(mapStateToProps, actions)(CreateChallenge);
